<template>
  <div id="pdfvuer">
    <div v-if="loading" class="flex items-center justify-center w-full py-10">
      <div
        style="border-top-color: transparent"
        class="w-20 h-20 border-4 border-color-link border-solid rounded-full animate-spin"
      />
      <p class="ml-4">{{ loadingProgress }}</p>
    </div>

    <div v-if="error" class="text-center py-10 text-red-500">
      {{ error }}
    </div>

    <pdf
      v-for="i in renderedPages"
      :id="i"
      :key="i"
      v-model:scale="scale"
      :src="pdfdata"
      :page="i"
      style="width: 100%; margin: 20px auto"
      :annotation="true"
      @link-clicked="handle_pdf_link"
    />

    <div v-if="hasMorePages" ref="loadMoreTrigger" class="h-10 text-center py-4">Loading more pages...</div>
  </div>
</template>

<script>
import pdfvuer from 'pdfvuer'

export default {
  name: 'PdfViewer',
  components: {
    pdf: pdfvuer,
  },
  props: {
    src: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      page: 1,
      numPages: 0,
      renderedPages: 0,
      pdfdata: undefined,
      errors: [],
      scale: 'page-width',
      loading: true,
      loadingProgress: 'Initializing...',
      error: null,
      observer: null,
      isLoadingMore: false,
    }
  },

  computed: {
    hasMorePages() {
      return this.renderedPages < this.numPages
    },
  },

  mounted() {
    this.getPdf()
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
    window.onscroll = null
  },

  methods: {
    handle_pdf_link(params) {
      const page = document.getElementById(String(params.pageNumber))
      if (page) {
        page.scrollIntoView({ behavior: 'smooth' })
      }
    },

    async getPdf() {
      try {
        this.loading = true
        this.loadingProgress = 'Fetching PDF...'
        this.error = null

        // Fetch with progress
        const response = await fetch(this.src)

        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`)
        }

        const contentLength = response.headers.get('content-length')
        const total = parseInt(contentLength, 10)
        let loaded = 0

        const reader = response.body.getReader()
        const chunks = []

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          chunks.push(value)
          loaded += value.length
          if (total) {
            const percent = Math.round((loaded / total) * 100)
            this.loadingProgress = `Loading PDF: ${percent}%`
          }
        }

        // Combine chunks
        const blob = new Blob(chunks, { type: 'application/pdf' })
        const arrayBuffer = await blob.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)

        this.loadingProgress = 'Processing PDF...'

        // Create loading task
        const loadingTask = pdfvuer.createLoadingTask({
          data: uint8Array,
          rangeChunkSize: 65536,
          disableAutoFetch: true,
          disableStream: false,
        })

        // Set pdfdata for the component
        this.pdfdata = loadingTask

        // Wait for PDF to load (use .then, not .promise)
        loadingTask
          .then((pdf) => {
            if (!pdf || !pdf.numPages) {
              throw new Error('PDF could not be loaded or has no pages')
            }
            this.numPages = pdf.numPages
            this.renderedPages = Math.min(3, pdf.numPages)
            this.loading = false

            this.$nextTick(() => {
              this.setupScrollTracking()
              this.setupIntersectionObserver()
            })
          })
          .catch((error) => {
            this.error = error.message || 'Failed to load PDF'
            this.loading = false
          })
      } catch (error) {
        this.error = error.message || 'Failed to load PDF'
        this.loading = false
      }
    },

    setupScrollTracking() {
      const self = this
      window.onscroll = function () {
        self.updateCurrentPage()
      }
    },

    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect()
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoadingMore && this.hasMorePages) {
              this.loadMorePages()
            }
          })
        },
        {
          rootMargin: '300px',
          threshold: 0,
        }
      )

      if (this.$refs.loadMoreTrigger) {
        this.observer.observe(this.$refs.loadMoreTrigger)
      }
    },

    loadMorePages() {
      if (this.isLoadingMore || !this.hasMorePages) return

      this.isLoadingMore = true

      setTimeout(() => {
        const toLoad = Math.min(3, this.numPages - this.renderedPages)
        this.renderedPages += toLoad
        this.isLoadingMore = false

        this.$nextTick(() => {
          if (this.hasMorePages && this.$refs.loadMoreTrigger) {
            this.observer.observe(this.$refs.loadMoreTrigger)
          }
        })
      }, 100)
    },

    updateCurrentPage() {
      let i = 1
      const count = Number(this.renderedPages)

      do {
        const currentEl = document.getElementById(i)
        const nextEl = document.getElementById(i + 1)

        if (
          currentEl &&
          window.pageYOffset >= this.findPos(currentEl) &&
          (!nextEl || window.pageYOffset < this.findPos(nextEl))
        ) {
          this.page = i
          break
        }
        i++
      } while (i <= count)

      const lastEl = document.getElementById(count)
      if (lastEl && window.pageYOffset >= this.findPos(lastEl)) {
        this.page = count
      }
    },

    findPos(obj) {
      return obj ? obj.offsetTop : 0
    },
  },
}
</script>

<style scoped>
#pdfvuer {
  min-height: 100vh;
}
</style>